import { DropdownObj } from "pages/Common/commonTypes"
import { array, number, object, string } from "yup"
import { useFormik } from "formik"
import { useGetDealDoneByUsersQuery, useGetTimeOfDayQuery } from "pages/Common/commonService"
import useProjectCategoryOptions from "hooks/useProjectCategoryOptions"
import React, { useMemo, useState } from "react"
import { ProjectFormPageProps } from "components/ProjectForm"
import {
    CHARGER_TYPE_OPTIONS,
    CONNECTION_TYPE_OPTIONS,
    CONNECTION_OWNED_BY_OPTIONS,
    FOUNDATION_TYPE_OPTIONS,
} from "enums/dropdownValues"
import { useSelector, useDispatch } from "react-redux"
import { TStore } from "store/Store"
import {
    setProjectId,
    setSurveyBasicDetailsForm,
    setSurveySpotImageNames,
    setSurveyElectricityBillNames,
    setSurveyPendingSpotFiles,
    setSurveyPendingElectricityBillFiles,
} from "components/ProjectForm/ProjectFormSlice"
import { useRaiseSurveyTicketMutation } from "pages/Operations/features/Survey/SurveyService"
import { enqueueSnackbar } from "notistack"
import { EProjectFormPages } from "components/ProjectForm/config"
import axios from "axios"
import {
    DocHubUrlInfo,
    SubmitFileInfo,
    useSubmitFinalUploadsMutation,
    useUploadDocumentsMutation,
} from "pages/Common/fileUploadService"

export enum ESurveyBasicDetailFields {
    SURVEY_NAME = "survey_name",
    CATEGORY = "category_id",
    BUSINESS_POC = "business_poc",
    CHARGER_DETAILS = "charger_details",
    AVAILABLE_LOAD = "available_load",
    CONNECTION_TYPE = "connection_type",
    CONNECTION_OWNED_BY = "connection_owned_by",
    NO_OF_SPOTS = "no_of_spots",
    VISIT_BY_DATE = "visit_by_date",
    TIME_SLOT = "time_slot",
    FOUNDATION_TYPE = "foundation_type",
    REMARKS = "remarks",
}

export enum ESurveyChargerDetailFields {
    CHARGER_TYPE = "charger_type",
    POWER_RATING = "power_rating",
    QUANTITY = "quantity",
}

export type SurveyChargerDetailRow = {
    [ESurveyChargerDetailFields.CHARGER_TYPE]: DropdownObj | null
    [ESurveyChargerDetailFields.POWER_RATING]: string
    [ESurveyChargerDetailFields.QUANTITY]: string
}

export type SurveyBasicDetailsFormValues = {
    [ESurveyBasicDetailFields.SURVEY_NAME]: string
    [ESurveyBasicDetailFields.CATEGORY]: DropdownObj | null
    [ESurveyBasicDetailFields.BUSINESS_POC]: DropdownObj | null
    [ESurveyBasicDetailFields.CHARGER_DETAILS]: SurveyChargerDetailRow[]
    [ESurveyBasicDetailFields.AVAILABLE_LOAD]: string
    [ESurveyBasicDetailFields.CONNECTION_TYPE]: DropdownObj | null
    [ESurveyBasicDetailFields.CONNECTION_OWNED_BY]: DropdownObj | null
    [ESurveyBasicDetailFields.NO_OF_SPOTS]: string
    [ESurveyBasicDetailFields.VISIT_BY_DATE]: string
    [ESurveyBasicDetailFields.TIME_SLOT]: DropdownObj | null
    [ESurveyBasicDetailFields.FOUNDATION_TYPE]: DropdownObj | null
    [ESurveyBasicDetailFields.REMARKS]: string
}

export const CHARGER_DETAIL_COLUMNS = [
    { key: ESurveyChargerDetailFields.CHARGER_TYPE, label: "Charger Type", type: "dropdown" },
    { key: ESurveyChargerDetailFields.POWER_RATING, label: "Power Rating", type: "number", unit: "kW" },
    { key: ESurveyChargerDetailFields.QUANTITY, label: "Quantity", type: "number" },
]

const EMPTY_CHARGER_ROW: SurveyChargerDetailRow = {
    [ESurveyChargerDetailFields.CHARGER_TYPE]: null,
    [ESurveyChargerDetailFields.POWER_RATING]: "",
    [ESurveyChargerDetailFields.QUANTITY]: "",
}

const initialValues: SurveyBasicDetailsFormValues = {
    [ESurveyBasicDetailFields.SURVEY_NAME]: "",
    [ESurveyBasicDetailFields.CATEGORY]: null,
    [ESurveyBasicDetailFields.BUSINESS_POC]: null,
    [ESurveyBasicDetailFields.CHARGER_DETAILS]: [{ ...EMPTY_CHARGER_ROW }],
    [ESurveyBasicDetailFields.AVAILABLE_LOAD]: "",
    [ESurveyBasicDetailFields.CONNECTION_TYPE]: null,
    [ESurveyBasicDetailFields.CONNECTION_OWNED_BY]: null,
    [ESurveyBasicDetailFields.NO_OF_SPOTS]: "",
    [ESurveyBasicDetailFields.VISIT_BY_DATE]: "",
    [ESurveyBasicDetailFields.TIME_SLOT]: null,
    [ESurveyBasicDetailFields.FOUNDATION_TYPE]: null,
    [ESurveyBasicDetailFields.REMARKS]: "",
}

const dropdownSchema = object({
    id: number().required(),
    title: string().required(),
}).nullable()

const stringDropdownSchema = object({
    id: string().required(),
    title: string().required(),
}).nullable()

const validationSchema = object({
    [ESurveyBasicDetailFields.SURVEY_NAME]: string().trim().required("Survey name is required"),
    // Mandatory per the PRD — a survey is categorised by whose site it is, which the template
    // cannot tell us, so it has to be a deliberate selection.
    [ESurveyBasicDetailFields.CATEGORY]: dropdownSchema.required("Project category is required"),
    [ESurveyBasicDetailFields.BUSINESS_POC]: dropdownSchema.required("Business POC is required"),
    [ESurveyBasicDetailFields.CHARGER_DETAILS]: array()
        .of(
            object({
                [ESurveyChargerDetailFields.CHARGER_TYPE]: stringDropdownSchema.required("Charger type is required"),
                [ESurveyChargerDetailFields.QUANTITY]: string()
                    .required("Quantity is required")
                    .test("positive", "Quantity must be greater than 0", val => Number(val) > 0),
                [ESurveyChargerDetailFields.POWER_RATING]: string()
                    .required("Power rating is required")
                    .test("positive", "Power rating must be greater than 0", val => Number(val) > 0),
            })
        )
        .min(1)
        .required(),
    [ESurveyBasicDetailFields.VISIT_BY_DATE]: string().trim().required("Visit by date is required"),
    [ESurveyBasicDetailFields.TIME_SLOT]: dropdownSchema.required("Time slot is required"),
    [ESurveyBasicDetailFields.AVAILABLE_LOAD]: string().optional(),
    [ESurveyBasicDetailFields.CONNECTION_TYPE]: object().nullable().optional(),
    [ESurveyBasicDetailFields.CONNECTION_OWNED_BY]: object().nullable().optional(),
    [ESurveyBasicDetailFields.NO_OF_SPOTS]: string().optional(),
    [ESurveyBasicDetailFields.FOUNDATION_TYPE]: object().nullable().optional(),
    [ESurveyBasicDetailFields.REMARKS]: string().optional(),
})

const useSurveyBasicDetails = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const {
        deal_details_form,
        account_n_sites_form,
        survey_basic_details_form,
        survey_spot_image_names,
        survey_electricity_bill_names,
        survey_pending_spot_files,
        survey_pending_electricity_bill_files,
    } = useSelector((state: TStore) => state.ProjectForm)
    const { user_id, loginData } = useSelector((state: any) => state.verifyLogin)

    const { data: dealDoneByUsersList } = useGetDealDoneByUsersQuery()
    // Raise-form picker: system categories are written only by automations, so they are excluded.
    const { options: categoryOptions, isFetching: isCategoriesFetching } = useProjectCategoryOptions()
    const { data: timeOfDayList } = useGetTimeOfDayQuery()
    const [raiseSurveyTicket, { isLoading: isSubmitting }] = useRaiseSurveyTicketMutation()
    const [uploadDocuments] = useUploadDocumentsMutation()
    const [submitFinalUploads] = useSubmitFinalUploadsMutation()
    const [isUploadingFiles, setIsUploadingFiles] = useState(false)

    const handleSpotImageChange = (files: FileList) => {
        const arr = Array.from(files)
        dispatch(setSurveyPendingSpotFiles(arr))
        dispatch(setSurveySpotImageNames(arr.map(f => f.name)))
    }

    const handleElectricityBillChange = (files: FileList) => {
        const arr = Array.from(files)
        dispatch(setSurveyPendingElectricityBillFiles(arr))
        dispatch(setSurveyElectricityBillNames(arr.map(f => f.name)))
    }

    // Upload a batch of files to S3 and return SubmitFileInfo[] for later association.
    // ticketId defaults to 0 for the Outright path (ticket not yet created).
    const uploadToS3 = async (files: File[], fileType: string, ticketId: number = 0): Promise<SubmitFileInfo[]> => {
        if (!files.length) return []
        const response = await uploadDocuments({
            ticket_id: ticketId,
            user_id,
            files: [{ file_type: fileType, file_name: files.map(f => f.name) }],
        }).unwrap()
        const urls: DocHubUrlInfo[] = response.urls[0]?.urls || []
        if (!urls.length) return []
        await Promise.all(
            urls.map((urlInfo: DocHubUrlInfo, i: number) =>
                axios.put(urlInfo.presigned_url, files[i], { headers: { "Content-Type": files[i]?.type } })
            )
        )
        return urls.map((urlInfo: DocHubUrlInfo) => ({
            file_url: urlInfo.file_url,
            doc_id: urlInfo.doc_id,
            report_id: urlInfo.report_id,
        }))
    }

    const businessPocOptions = useMemo(
        () =>
            dealDoneByUsersList?.map((user: any) => ({
                id: user.id,
                title: user.name,
            })) || [],
        [dealDoneByUsersList]
    )

    const timeSlotOptions = useMemo(
        () => timeOfDayList?.data?.map(i => ({ id: i.id, title: i.name })) || [],
        [timeOfDayList]
    )

    const formik = useFormik<SurveyBasicDetailsFormValues>({
        initialValues: survey_basic_details_form ? { ...survey_basic_details_form } : initialValues,
        validationSchema,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: handleSubmit,
    })

    const updateChargerRow = (rowIndex: number, key: keyof SurveyChargerDetailRow, value: any) => {
        const updated = [...formik.values[ESurveyBasicDetailFields.CHARGER_DETAILS]]
        updated[rowIndex] = { ...updated[rowIndex], [key]: value }
        formik.setFieldValue(ESurveyBasicDetailFields.CHARGER_DETAILS, updated)
        formik.setFieldError(`${ESurveyBasicDetailFields.CHARGER_DETAILS}[${rowIndex}].${key}`, undefined)
    }

    const handleAddChargerRow = () => {
        formik.setFieldValue(ESurveyBasicDetailFields.CHARGER_DETAILS, [
            ...formik.values[ESurveyBasicDetailFields.CHARGER_DETAILS],
            { ...EMPTY_CHARGER_ROW },
        ])
    }

    const handleDeleteChargerRow = (rowIndex: number) => {
        const updated = [...formik.values[ESurveyBasicDetailFields.CHARGER_DETAILS]]
        updated.splice(rowIndex, 1)
        formik.setFieldValue(ESurveyBasicDetailFields.CHARGER_DETAILS, updated)
    }

    const handleAssignToMe = () => {
        const myOption = businessPocOptions.find(opt => opt.id === user_id)
        if (myOption) {
            formik.setFieldValue(ESurveyBasicDetailFields.BUSINESS_POC, myOption)
        } else if (loginData?.user_data) {
            formik.setFieldValue(ESurveyBasicDetailFields.BUSINESS_POC, {
                id: user_id,
                title: loginData.user_data.name,
            })
        }
    }

    const buildApiPayload = (values: SurveyBasicDetailsFormValues) => ({
        template_id: deal_details_form.template_id?.id as number,
        destination_site_id: account_n_sites_form.destination_site_id || undefined,
        accounts: account_n_sites_form.account_roles.map(role => ({
            account_id: role.account_id,
            account_type_id: role.account_type_id,
            billing_address_id: role.billing_address_id,
            contact_id: role.contact_id,
            contact_type_id: role.contact_type_id,
        })),
        ticket_name: values[ESurveyBasicDetailFields.SURVEY_NAME],
        category_id: values[ESurveyBasicDetailFields.CATEGORY]?.id as number | undefined,
        business_poc_id: values[ESurveyBasicDetailFields.BUSINESS_POC]?.id as number | undefined,
        chargers: values[ESurveyBasicDetailFields.CHARGER_DETAILS].map(charger => ({
            charger_type: String((charger.charger_type as DropdownObj)?.id),
            quantity: parseInt(charger.quantity) || 0,
            power_rating: charger.power_rating ? `${charger.power_rating}` : undefined,
        })),
        available_load: values[ESurveyBasicDetailFields.AVAILABLE_LOAD] || undefined,
        connection_type: values[ESurveyBasicDetailFields.CONNECTION_TYPE]?.id as string | undefined,
        connection_owned_by: values[ESurveyBasicDetailFields.CONNECTION_OWNED_BY]?.id as string | undefined,
        no_of_spots: values[ESurveyBasicDetailFields.NO_OF_SPOTS]
            ? parseInt(values[ESurveyBasicDetailFields.NO_OF_SPOTS])
            : undefined,
        foundation_type: values[ESurveyBasicDetailFields.FOUNDATION_TYPE]?.id as string | undefined,
        remarks: values[ESurveyBasicDetailFields.REMARKS] || undefined,
        visit_by_date: values[ESurveyBasicDetailFields.VISIT_BY_DATE],
        time_slot: values[ESurveyBasicDetailFields.TIME_SLOT]?.id as number,
    })

    async function handleSubmit() {
        const values = formik.values
        const isLastPage = !props.pages?.includes(EProjectFormPages.PAYMENT_DETAILS)

        try {
            if (!isLastPage) {
                dispatch(setSurveyBasicDetailsForm(values))
                props.handleNextStep()
                return
            }

            setIsUploadingFiles(true)

            const result = await raiseSurveyTicket(buildApiPayload(values)).unwrap()
            dispatch(setProjectId(result.project_id))
            dispatch(setSurveyBasicDetailsForm(values))

            const [spotInfos, elecInfos] = await Promise.all([
                uploadToS3(survey_pending_spot_files, "survey_spot_image", result.ticket_id),
                uploadToS3(survey_pending_electricity_bill_files, "survey_others", result.ticket_id),
            ])

            const allInfos = [...spotInfos, ...elecInfos]
            if (allInfos.length) {
                await submitFinalUploads({ ticket_id: result.ticket_id, file_infos: allInfos }).unwrap()
            }

            dispatch(setSurveyPendingSpotFiles([]))
            dispatch(setSurveyPendingElectricityBillFiles([]))

            props.handleNextStep(result.project_id)
            enqueueSnackbar("Survey ticket created successfully", { variant: "success" })
        } catch (error: any) {
            enqueueSnackbar(error?.data?.error || error?.data?.message || "Failed to submit survey", {
                variant: "error",
            })
        } finally {
            setIsUploadingFiles(false)
        }
    }

    const hasError = (field: ESurveyBasicDetailFields) => Boolean(formik.submitCount > 0 && formik.errors[field])

    const hasChargerError = (rowIndex: number, field: ESurveyChargerDetailFields): boolean => {
        if (formik.submitCount === 0) return false
        const chargerErrors = formik.errors[ESurveyBasicDetailFields.CHARGER_DETAILS] as any[]
        return Boolean(chargerErrors?.[rowIndex]?.[field])
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        formik.setFieldError(e.target.name, undefined)
        formik.handleChange(e)
    }

    const handleDropdownChange = (field: ESurveyBasicDetailFields, value: any) => {
        formik.setFieldError(field, undefined)
        formik.setFieldValue(field, value)
    }

    return {
        states: {
            formik,
            businessPocOptions,
            categoryOptions,
            isCategoriesFetching,
            timeSlotOptions,
            CHARGER_TYPE_OPTIONS,
            CONNECTION_TYPE_OPTIONS,
            CONNECTION_OWNED_BY_OPTIONS,
            FOUNDATION_TYPE_OPTIONS,
            isSubmitting: isSubmitting || isUploadingFiles,
            spotImageNames: survey_spot_image_names,
            electricityBillNames: survey_electricity_bill_names,
        },
        functions: {
            handleAddChargerRow,
            handleDeleteChargerRow,
            updateChargerRow,
            handleAssignToMe,
            hasError,
            hasChargerError,
            handleChange,
            handleDropdownChange,
            handleSpotImageChange,
            handleElectricityBillChange,
        },
    }
}

export default useSurveyBasicDetails
